import { UUID_PATTERN } from "@p0security/integrations-entra-id-shared";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import {
  isAzureFunctionAppResourceId,
  isAzureRoleDefinitionResourceId,
  isAzureVirtualMachineResourceId,
} from "./asset.ts";
import {
  VIRTUAL_MACHINE_ADMINISTRATOR_LOGIN_ROLE,
  VIRTUAL_MACHINE_USER_LOGIN_ROLE,
} from "./constants.ts";

const ROLE_DEFINITION_ID_HINT =
  "Enter a valid role definition resource ID, e.g. /subscriptions/<id>/providers/Microsoft.Authorization/roleDefinitions/<guid>.";

/** The role P0 assigns to a user for standard (non-sudo) access. It is granted
 * at the connection point (the bastion or jump host) and, for standard
 * requests, at the target VM. Point it at a built-in role (Azure's
 * "Virtual Machine User Login" is the recommended default), an existing custom
 * role, or a new one. */
const standardAccessRoleIdElement = (description: string) =>
  Element({
    type: "string",
    label: "Standard user role id",
    description,
    validator: async (_id, field) =>
      isAzureRoleDefinitionResourceId(field?.trim())
        ? undefined
        : ROLE_DEFINITION_ID_HINT,
  });

/** The role P0 assigns to a user for sudo access, granted at the target VM when
 * a request opts into sudo. Point it at a built-in role (Azure's "Virtual
 * Machine Administrator Login" is the recommended default), an existing custom
 * role, or a new one. */
const adminAccessRoleIdElement = (description: string) =>
  Element({
    type: "string",
    label: "Admin user role id",
    description,
    validator: async (_id, field) =>
      isAzureRoleDefinitionResourceId(field?.trim())
        ? undefined
        : ROLE_DEFINITION_ID_HINT,
  });

// Nested under the "Azure bastion host" option: which bastion host to use.
const bastionHostOptions = {
  single: {
    label: "Single bastion host",
    description:
      "A single Azure Bastion host for subscription that users connect to the vms.",
    maturity: "ga" as const,
    schema: {
      bastionId: Element({
        type: "string",
        label: "Bastion host ID",
        description: "The Azure Bastion host ID.",
      }),
    },
  },
  subscription: {
    label: "Subscription bastion host",
    description: "Azure Bastion hosts configured for different subscriptions.",
    maturity: "ga" as const,
    schema: {
      subscriptionId: Element({
        type: "dynamic",
        label: "Subscription ID",
        description:
          "The subscription ID for existing Azure Bastion host configuration.",
      }),
    },
  },
};

// Top-level connection type: an Azure Bastion host or a custom jump host.
// Both take the role definition IDs that P0 assigns to a connecting user:
// standardAccessRoleId (at the connection point and — for standard requests —
// the target VM) and adminAccessRoleId (at the target VM for sudo requests).
// Both are required. The required permissions differ by connection type,
// so each option documents its own recommendation: the Bastion option needs a
// fuller set (Bastion access plus VM login), while the jump host option needs
// only the smaller jump-host-scoped set.
const bastionOptions = {
  azureBastion: {
    label: "Azure bastion host",
    description: "Provision SSH access through a managed Azure Bastion host.",
    maturity: "ga" as const,
    schema: {
      standardAccessRoleId: standardAccessRoleIdElement(
        `The role granted to a user for standard access through this Azure Bastion host. It must allow the user to reach and log in to the target VM through the bastion. Recommended default: the built-in "${VIRTUAL_MACHINE_USER_LOGIN_ROLE.name}" role.`
      ),
      adminAccessRoleId: adminAccessRoleIdElement(
        `The role granted to a user for sudo access to the target VM. Recommended default: the built-in "${VIRTUAL_MACHINE_ADMINISTRATOR_LOGIN_ROLE.name}" role.`
      ),
      bastionHost: Element({
        label: "Which Azure Bastion host should P0 use to provision access?",
        type: "select" as const,
        default: "single",
        options: bastionHostOptions,
      }),
    },
  },
  jumpHost: {
    label: "Custom jump host",
    description:
      "Predefine a virtual machine behind a virtual network to act as a jump host.",
    maturity: "ga" as const,
    schema: {
      virtualMachineId: Element({
        type: "string",
        label: "Virtual machine resource ID",
        description:
          "The Azure resource ID of the jump host VM, e.g. /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Compute/virtualMachines/<name>.",
        validator: async (_id, field) =>
          isAzureVirtualMachineResourceId(field)
            ? undefined
            : "Enter a valid virtual machine resource ID, e.g. /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Compute/virtualMachines/<name>.",
      }),
      standardAccessRoleId: standardAccessRoleIdElement(
        `The role granted to a user for standard access through this jump host, so they can reach and log in to a target virtual machine. This role is scoped to jump-host management and can be smaller than the Bastion equivalent. Recommended default: the built-in "${VIRTUAL_MACHINE_USER_LOGIN_ROLE.name}" role.`
      ),
      adminAccessRoleId: adminAccessRoleIdElement(
        `The role granted to a user for sudo access to the target VM. Recommended default: the built-in "${VIRTUAL_MACHINE_ADMINISTRATOR_LOGIN_ROLE.name}" role.`
      ),
      // Resolved from the VM at install time and saved here so access requests
      // can read it from the install doc instead of looking it up each time.
      ip: Element({
        type: "hidden",
        label: "Jump host IP address",
      }),
    },
  },
};

export const AzureComponents = InstallSpec({
  root: ItemComponent({
    label: "Microsoft Azure",
    description: "Microsoft Azure",
    type: "singleton",
    maturity: "ga",
    schema: {
      directoryId: Element({
        type: "string",
        label: "Tenant ID",
      }),
      serviceAccountEmail: Element({
        label: "P0 service account human-readable identifier",
        type: "hidden",
      }),
      serviceAccountId: Element({
        label: "P0 service account machine identifier",
        type: "hidden",
      }),
    },
  }),
  "azure-app": ItemComponent({
    label: "Azure app registration",
    description:
      "Allows P0 to authenticate and authorize with Microsoft Azure to provision access.",
    type: "singleton",
    maturity: "ga",
    schema: {
      clientId: Element({
        type: "string",
        label: "Application ID",
        description: "The application (client) ID of the app registration.",
      }),
      clientServicePrincipalId: Element({
        type: "hidden",
        label: "Service principal ID",
      }),
    },
  }),
  "iam-write": ItemComponent({
    label: "IAM management",
    description:
      "Allows P0 to grant and revoke resource access using Azure role-based access control (RBAC).",
    type: "string",
    maturity: "ga",
    schema: {
      resourceId: Element({
        type: "hidden",
        label: "Subscription resource ID",
      }),
    },
  }),
  "jump-host": ItemComponent({
    label: "Jump host management",
    description:
      "Allows P0 to send privileged commands (e.g. terminating a live SSH session) to your jump hosts through an Azure Function.",
    type: "singleton",
    maturity: "beta",
    schema: {
      // Written by the assembler: the tenant this install belongs to (used to
      // guard against cross-organization service-account collisions) and the
      // P0 service account whose identity the customer's app registration
      // trusts via workload identity federation.
      directoryId: Element({
        type: "hidden",
        label: "Tenant ID",
      }),
      serviceAccountEmail: Element({
        type: "hidden",
        label: "P0 service account human-readable identifier",
      }),
      serviceAccountId: Element({
        type: "hidden",
        label: "P0 service account machine identifier",
      }),
      clientId: Element({
        type: "string",
        label: "Application client ID",
        description:
          "The app registration P0 uses (via workload identity federation) to authenticate to the jump host management Azure Function.",
        validator: async (_id, field) => {
          if (!field) return "Application client ID is required.";
          if (!field.match(UUID_PATTERN))
            return "Expected application client ID (UUID format).";
        },
      }),
      functionAppResourceId: Element({
        type: "string",
        label: "Function app resource ID",
        description:
          "The Azure resource ID of the Function App P0 sends requests to, e.g. /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Web/sites/<name>.",
        validator: async (_id, field) =>
          isAzureFunctionAppResourceId(field)
            ? undefined
            : "Enter a valid Function App resource ID, e.g. /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Web/sites/<name>.",
      }),
    },
  }),
  "iam-assessment": ItemComponent({
    label: "IAM assessment",
    description:
      "Allows P0 to assess and analyze Azure role assignments, permissions, and resource access for security compliance.",
    type: "string",
    maturity: "beta",
    schema: {
      resourceId: Element({
        type: "hidden",
        label: "Subscription resource ID",
      }),
    },
  }),
  "bastion-host": ItemComponent({
    label: "Bastion host",
    description:
      "Allows P0 to grant access to Azure VMs using a secure bastion host or jump host.",
    type: "dynamic",
    maturity: "ga",
    schema: {
      bastion: Element({
        label: "How should P0 connect to your Azure VMs to provision access?",
        type: "select" as const,
        default: "azureBastion",
        // Chosen on the "Installing a new subscription" screen (alongside the
        // subscription identifier) so it can drive the subsequent steps; the
        // per-option fields below are filled on the configure step.
        step: "new",
        options: bastionOptions,
      }),
    },
  }),
});
